import { useEffect, useState, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { Toaster } from 'sonner'
import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/store/authStore'
import { useTimesheetStore } from '@/modules/timesheet/stores/timesheetStore'
import { fetchRosterIdentityByEmail } from '@/modules/timesheet/services/timesheetSupabaseService'
import { rosterRowToCurrentUser, resolveRosterBindOutcome } from '@/lib/productionIdentity'
import { isProductSurface } from '@/config/productSurface'
import { ssoJitMode } from '@/config/authConfig'
import { resolveSkipAuth } from '@/config/authSkip'
import { getDemoProfile, isDemoAuthAccount } from '@/types/demoProfiles'
import { captureShareInvite } from '@/services/projectShareService'
import { getPortfolioById, getProjectById } from '@/services/dataService'
import { PROJECT_URL_PARAM, projectIdFromUrl } from '@/lib/projectUrl'
import { useAuthInitialize } from '@/hooks/useAuthorization'
import { isModuleEnabled } from '@/config/tesseractConfig'
import { FeatureGate } from '@/components/common/FeatureGate'
import { RequireCapability } from '@/components/common/RequireCapability'
// Shell-level surfaces stay EAGER so the shell/landing paint immediately and
// remain mounted during lazy route transitions.
import { AppShell } from '@/components/shell/AppShell'
import { ContextSelector } from '@/components/shell/ContextSelector'
import { LoginPage } from '@/components/auth/LoginPage'
import { AccessGate } from '@/components/auth/AccessGate'
import { ModuleRouteGuard } from '@/components/auth/ModuleRouteGuard'
import { AgileOnlyRouteGuard } from '@/components/auth/AgileOnlyRouteGuard'

// Route page components are lazy-loaded so they split into their own chunks
// instead of inflating the main bundle. Pattern mirrors CLMRoutesProvider.
const Dashboard = lazy(() => import('@/components/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })))
const ProjectList = lazy(() => import('@/components/projects/ProjectList').then((m) => ({ default: m.ProjectList })))
const ProjectDetail = lazy(() => import('@/components/projects/ProjectDetail').then((m) => ({ default: m.ProjectDetail })))
const ProjectTeamPage = lazy(() => import('@/components/projects/team/ProjectTeamPage').then((m) => ({ default: m.ProjectTeamPage })))
const ProjectAttributesPage = lazy(() => import('@/components/projects/attributes/ProjectAttributesPage').then((m) => ({ default: m.ProjectAttributesPage })))
const ProjectLocationsPage = lazy(() => import('@/components/projects/locations/ProjectLocationsPage').then((m) => ({ default: m.ProjectLocationsPage })))
const ProjectStartupChecklistPage = lazy(() => import('@/components/projects/startup/ProjectStartupChecklistPage').then((m) => ({ default: m.ProjectStartupChecklistPage })))
const SapDeploymentPage = lazy(() => import('@/components/sap-deployment/SapDeploymentPage').then((m) => ({ default: m.SapDeploymentPage })))
const BudgetReleases = lazy(() => import('@/components/budget/BudgetReleases').then((m) => ({ default: m.BudgetReleases })))
const ResourceManagement = lazy(() => import('@/components/resources/ResourceManagement').then((m) => ({ default: m.ResourceManagement })))
// #67d module-aware build: each __MOD_*__ flag is a compile-time literal injected
// from VITE_MODULES (vite.config.ts → define). When false, the ternary's import()
// is dead code, so Rollup drops that module's chunk from the bundle. ModuleDisabled
// is the inert stand-in; routes are also runtime-gated by isModuleEnabled, so a
// disabled module never renders.
const ModuleDisabled = () => null
const SupplyChainManagement = __MOD_SUPPLYCHAIN__ ? lazy(() => import('@/components/materials/MaterialManagement').then((m) => ({ default: m.SupplyChainManagement }))) : ModuleDisabled
const PersonnelManagement = __MOD_PERSONNEL__ ? lazy(() => import('@/components/personnel/PersonnelManagement').then((m) => ({ default: m.PersonnelManagement }))) : ModuleDisabled
const ProjectRequests = lazy(() => import('@/components/requests/ProjectRequests').then((m) => ({ default: m.ProjectRequests })))
const OrganizationsHome = lazy(() => import('@/components/organizations/OrganizationsHome').then((m) => ({ default: m.OrganizationsHome })))
const ResourceDemandsPage = lazy(() => import('@/components/organizations/ResourceDemandsPage').then((m) => ({ default: m.ResourceDemandsPage })))
const SkillApprovalsPage = lazy(() => import('@/components/my-work/SkillApprovalsPage').then((m) => ({ default: m.SkillApprovalsPage })))
const ProjectRequestDetail = lazy(() => import('@/components/requests/ProjectRequestDetail').then((m) => ({ default: m.ProjectRequestDetail })))
const NewProjectRequest = lazy(() => import('@/components/requests/NewProjectRequest').then((m) => ({ default: m.NewProjectRequest })))
const Priorities = lazy(() => import('@/components/priorities/Priorities').then((m) => ({ default: m.Priorities })))
const RoiModule = lazy(() => import('@/components/roi/RoiModule').then((m) => ({ default: m.RoiModule })))
const LessonsLearnedPage = lazy(() => import('@/components/lessons/LessonsLearnedPage').then((m) => ({ default: m.LessonsLearnedPage })))
const ScenarioPlanning = lazy(() => import('@/components/scenarios/ScenarioPlanning').then((m) => ({ default: m.ScenarioPlanning })))
const Workflows = lazy(() => import('@/components/workflows/Workflows').then((m) => ({ default: m.Workflows })))
const Analysis = lazy(() => import('@/components/analysis/Analysis').then((m) => ({ default: m.Analysis })))
const RiskRegister = lazy(() => import('@/components/risks/RiskRegister').then((m) => ({ default: m.RiskRegister })))
const SchedulingPage = lazy(() => import('@/components/scheduling/SchedulingPage').then((m) => ({ default: m.SchedulingPage })))
const WbsHierarchyPage = lazy(() => import('@/components/hierarchy/WbsHierarchyPage').then((m) => ({ default: m.WbsHierarchyPage })))
const ImplementationHub = lazy(() => import('@/components/implementation/ImplementationHub').then((m) => ({ default: m.ImplementationHub })))
const HierarchyLinkagesPage = lazy(() => import('@/components/hierarchy/HierarchyLinkagesPage').then((m) => ({ default: m.HierarchyLinkagesPage })))
const BaselineManagement = lazy(() => import('@/components/budget/BaselineManagement').then((m) => ({ default: m.BaselineManagement })))
const EvmWorkbench = lazy(() => import('@/components/evm/EvmWorkbench').then((m) => ({ default: m.EvmWorkbench })))
const ReportsPage = lazy(() => import('@/components/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })))
const ForecastManagement = lazy(() => import('@/components/forecasting/ForecastManagement').then((m) => ({ default: m.ForecastManagement })))
const RatesManagement = lazy(() => import('@/components/rates/RatesManagement').then((m) => ({ default: m.RatesManagement })))
const NotificationsCockpit = lazy(() => import('@/components/notifications/NotificationsCockpit').then((m) => ({ default: m.NotificationsCockpit })))
const HelpCenterPage = lazy(() => import('@/components/help/HelpCenterPage').then((m) => ({ default: m.HelpCenterPage })))
const ApiExplorer = lazy(() => import('@/components/api-explorer/ApiExplorer').then((m) => ({ default: m.ApiExplorer })))
const AnalyticsRoutesProvider = lazy(() => import('@/modules/analytics/pages/AnalyticsRoutesProvider').then((m) => ({ default: m.AnalyticsRoutesProvider })))
const CLMRoutesProvider = __MOD_CLM__ ? lazy(() => import('@/modules/clm/pages/CLMRoutesProvider').then((m) => ({ default: m.CLMRoutesProvider }))) : ModuleDisabled
const TimesheetRoutesProvider = __MOD_TIMESHEET__ ? lazy(() => import('@/modules/timesheet').then((m) => ({ default: m.TimesheetRoutesProvider }))) : ModuleDisabled
const WorkflowManagementRoutesProvider = __MOD_WORKFLOW_MANAGEMENT__ ? lazy(() => import('@/modules/workflow-management').then((m) => ({ default: m.WorkflowManagementRoutesProvider }))) : ModuleDisabled
const CostManagementRoutesProvider = __MOD_COST_MANAGEMENT__ ? lazy(() => import('@/modules/cost-management').then((m) => ({ default: m.CostManagementRoutesProvider }))) : ModuleDisabled
const CrmRoutesProvider = __MOD_CRM__ ? lazy(() => import('@/modules/crm').then((m) => ({ default: m.CrmRoutesProvider }))) : ModuleDisabled
const ItTicketingRoutesProvider = __MOD_IT_TICKETING__ ? lazy(() => import('@/modules/it-ticketing').then((m) => ({ default: m.ItTicketingRoutesProvider }))) : ModuleDisabled
const ConfigDashboard = lazy(() => import('@/components/config/ConfigDashboard').then((m) => ({ default: m.ConfigDashboard })))
const ConfigClientSetup = lazy(() => import('@/components/config/ConfigClientSetup').then((m) => ({ default: m.ConfigClientSetup })))
const ConfigInstanceSetup = lazy(() => import('@/components/config/ConfigInstanceSetup').then((m) => ({ default: m.ConfigInstanceSetup })))
const ConfigUserManagement = lazy(() => import('@/components/config/ConfigUserManagement').then((m) => ({ default: m.ConfigUserManagement })))
const ConfigOrgDirectory = lazy(() => import('@/components/config/ConfigOrgDirectory').then((m) => ({ default: m.ConfigOrgDirectory })))
const ConfigRoleAssignments = lazy(() => import('@/components/config/ConfigRoleAssignments').then((m) => ({ default: m.ConfigRoleAssignments })))
const ConfigPolicies = lazy(() => import('@/components/config/ConfigPolicies').then((m) => ({ default: m.ConfigPolicies })))
const ConfigSops = lazy(() => import('@/components/config/ConfigSops').then((m) => ({ default: m.ConfigSops })))
const ConfigAuditLog = lazy(() => import('@/components/config/ConfigAuditLog').then((m) => ({ default: m.ConfigAuditLog })))
const ConfigUiMappings = lazy(() => import('@/components/config/ConfigUiMappings').then((m) => ({ default: m.ConfigUiMappings })))
const ConfigProjectLifecycle = lazy(() => import('@/components/config/ConfigProjectLifecycle').then((m) => ({ default: m.ConfigProjectLifecycle })))
const ConfigExpertise = lazy(() => import('@/components/config/ConfigExpertise').then((m) => ({ default: m.ConfigExpertise })))
const ConfigDisciplines = lazy(() => import('@/components/config/ConfigDisciplines').then((m) => ({ default: m.ConfigDisciplines })))
const ConfigResourcePlanning = lazy(() => import('@/components/config/ConfigResourcePlanning').then((m) => ({ default: m.ConfigResourcePlanning })))
const ConfigResourceFields = lazy(() => import('@/components/config/ConfigResourceFields').then((m) => ({ default: m.ConfigResourceFields })))
const ConfigCostPlanningAccounts = lazy(() => import('@/components/config/ConfigCostPlanningAccounts').then((m) => ({ default: m.ConfigCostPlanningAccounts })))
const ConfigChartOfAccounts = lazy(() => import('@/components/config/ConfigChartOfAccounts').then((m) => ({ default: m.ConfigChartOfAccounts })))
const ConfigDemoLibrary = lazy(() => import('@/components/config/ConfigDemoLibrary').then((m) => ({ default: m.ConfigDemoLibrary })))
const ConfigAccessRequests = lazy(() => import('@/components/config/ConfigAccessRequests').then((m) => ({ default: m.ConfigAccessRequests })))
const ConfigFeatureFlags = lazy(() => import('@/components/config/ConfigFeatureFlags').then((m) => ({ default: m.ConfigFeatureFlags })))
const ConfigCapabilities = lazy(() => import('@/components/config/ConfigCapabilities').then((m) => ({ default: m.ConfigCapabilities })))
// Agile Delivery surfaces (Hybrid Waterfall/Agile feature). Routes stay
// registered for the nav-coverage gates; RequireCapability makes them inert
// when the agile.enabled capability is off.
const BacklogPage = lazy(() => import('@/components/agile/BacklogPage').then((m) => ({ default: m.BacklogPage })))
const WorkItemDetailPage = lazy(() => import('@/components/agile/WorkItemDetailPage').then((m) => ({ default: m.WorkItemDetailPage })))
const BoardPage = lazy(() => import('@/components/agile/BoardPage').then((m) => ({ default: m.BoardPage })))
const SprintsPage = lazy(() => import('@/components/agile/SprintsPage').then((m) => ({ default: m.SprintsPage })))
const RoadmapPage = lazy(() => import('@/components/agile/RoadmapPage').then((m) => ({ default: m.RoadmapPage })))
const AgileInsightsPage = lazy(() => import('@/components/agile/AgileInsightsPage').then((m) => ({ default: m.AgileInsightsPage })))
const OnboardingPage = lazy(() => import('@/components/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })))
const ConfigCertifications = lazy(() => import('@/components/config/ConfigCertifications').then((m) => ({ default: m.ConfigCertifications })))
const ConfigTools = lazy(() => import('@/components/config/ConfigTools').then((m) => ({ default: m.ConfigTools })))
const ConfigLocations = lazy(() => import('@/components/config/ConfigLocations').then((m) => ({ default: m.ConfigLocations })))
const ConfigProjectRoles = lazy(() => import('@/components/config/ConfigProjectRoles').then((m) => ({ default: m.ConfigProjectRoles })))
const ConfigBudgetCategories = lazy(() => import('@/components/config/ConfigBudgetCategories').then((m) => ({ default: m.ConfigBudgetCategories })))
const ConfigAwardValueCategories = lazy(() => import('@/components/config/ConfigAwardValueCategories').then((m) => ({ default: m.ConfigAwardValueCategories })))
const ConfigManufacturingTime = lazy(() => import('@/components/config/ConfigManufacturingTime').then((m) => ({ default: m.ConfigManufacturingTime })))
const ConfigTimeEvaluationRules = lazy(() => import('@/components/config/ConfigTimeEvaluationRules').then((m) => ({ default: m.ConfigTimeEvaluationRules })))
const ConfigWbsAttributeLibrary = lazy(() => import('@/components/config/ConfigWbsAttributeLibrary').then((m) => ({ default: m.ConfigWbsAttributeLibrary })))
const SapIntegrationPage = lazy(() => import('@/components/sap-integration/SapIntegrationPage').then((m) => ({ default: m.SapIntegrationPage })))
const ConfigOrgImport = lazy(() => import('@/modules/orgImport/ConfigOrgImport').then((m) => ({ default: m.ConfigOrgImport })))
const ErpSyncPage = lazy(() => import('@/components/sap-integration/ErpSyncPage').then((m) => ({ default: m.ErpSyncPage })))
const EstimateImportPage = lazy(() => import('@/modules/estimate-import/EstimateImportPage').then((m) => ({ default: m.EstimateImportPage })))
const CurrencyRatesPage = lazy(() => import('@/components/sap-integration/CurrencyRatesPage').then((m) => ({ default: m.CurrencyRatesPage })))
const SupplyChainPage = __MOD_SUPPLYCHAIN__ ? lazy(() => import('@/components/supply-chain/SupplyChainPage').then((m) => ({ default: m.SupplyChainPage }))) : ModuleDisabled
const NewPortfolio = lazy(() => import('@/components/portfolios/NewPortfolio').then((m) => ({ default: m.NewPortfolio })))
const MilestoneAnalyzer = lazy(() => import('@/components/portfolios/MilestoneAnalyzer').then((m) => ({ default: m.MilestoneAnalyzer })))
const ProgramReview = lazy(() => import('@/components/programs/ProgramReview').then((m) => ({ default: m.ProgramReview })))
const MyWorkPage = lazy(() => import('@/components/my-work/MyWorkPage').then((m) => ({ default: m.MyWorkPage })))
const MyProfilePage = lazy(() => import('@/components/my-work/MyProfilePage').then((m) => ({ default: m.MyProfilePage })))
const ActionInboxPage = lazy(() => import('@/components/my-work/ActionInboxPage').then((m) => ({ default: m.ActionInboxPage })))
const MyTasksPage = lazy(() => import('@/components/my-work/MyTasksPage').then((m) => ({ default: m.MyTasksPage })))
const MyRisksPage = lazy(() => import('@/components/my-work/MyRisksPage').then((m) => ({ default: m.MyRisksPage })))
const MyCalendarPage = lazy(() => import('@/components/my-work/MyCalendarPage').then((m) => ({ default: m.MyCalendarPage })))
const PublicFormPage = lazy(() => import('@/components/forms/PublicFormPage').then((m) => ({ default: m.PublicFormPage })))
const InviteFormPage = lazy(() => import('@/components/forms/InviteFormPage').then((m) => ({ default: m.InviteFormPage })))
const SharedAgilePage = lazy(() => import('@/components/agile/SharedAgilePage').then((m) => ({ default: m.SharedAgilePage })))
const SharedRaidPage = lazy(() => import('@/components/risks/SharedRaidPage').then((m) => ({ default: m.SharedRaidPage })))
const TesterPortalPage = lazy(() => import('@/modules/testing/TesterPortalPage').then((m) => ({ default: m.TesterPortalPage })))
const SharedSchedulePage = lazy(() => import('@/components/scheduling/SharedSchedulePage').then((m) => ({ default: m.SharedSchedulePage })))

/** Lightweight content-area skeleton shown while a lazy route chunk loads. */
function PageSkeleton() {
  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="h-8 w-48 rounded bg-slate-200 animate-pulse mb-6" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />
        ))}
      </div>
      <div className="h-[400px] rounded-xl bg-slate-100 animate-pulse" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const contextSelected = useAppStore((s) => s.contextSelected)
  const selectedProject = useAppStore((s) => s.selectedProject)
  const managementContext = useAppStore((s) => s.managementContext)
  const location = useLocation()
  // A bookmarked or shared project page names its project in the URL. Open
  // that project when none is selected, or when the `project` param names a
  // different one. A /projects/:id page switches projects itself, so its path
  // id is only used when nothing is selected.
  const urlProjectId = projectIdFromUrl(location.pathname, location.search)
  const fromParam = new URLSearchParams(location.search).has(PROJECT_URL_PARAM)
  if (urlProjectId && (!selectedProject || (fromParam && selectedProject.id !== urlProjectId))) {
    return <RestoreProjectFromUrl projectId={urlProjectId} />
  }
  // Allow config context without portfolio selection — either via the
  // managementContext flag, or because the URL itself is a /config/* path
  // (in case the user lands there without going through the Configuration tile).
  // Timesheet + Contracts (CLM) + Supply Chain are org-wide module surfaces that
  // don't require a selected portfolio, so they're allow-listed too — otherwise
  // clicking their tile before the portfolio hierarchy finishes loading bounces
  // home, because the selectPortfolio(hierarchy[0]) context hack races with the
  // data load and leaves contextSelected=false.
  if (managementContext === 'configuration' || managementContext === 'mywork' || managementContext === 'personnel' || managementContext === 'workflow-management' || managementContext === 'organizational' || managementContext === 'cost' || managementContext === 'crm' || managementContext === 'it-ticketing' || location.pathname.startsWith('/cost') || location.pathname.startsWith('/crm') || location.pathname.startsWith('/it-ticketing') || location.pathname.startsWith('/config/') || location.pathname === '/sap-integration' || location.pathname.startsWith('/supply-chain') || location.pathname.startsWith('/timesheet') || location.pathname.startsWith('/contracts') || location.pathname.startsWith('/my-work') || location.pathname.startsWith('/workflow-management') || location.pathname === '/personnel' || location.pathname.startsWith('/organizations') || location.pathname === '/skill-approvals' || location.pathname.startsWith('/projects/new')) return <>{children}</>
  // A selected project should be enough to act as "context selected" — Direct
  // startup creates a draft project that may not be attached to a portfolio
  // (portfolio_id is nullable on `projects`). Without this, the WBS Manager
  // link from the wizard bounces back to /.
  if (!contextSelected && !selectedProject) return <Navigate to="/" replace />
  return <>{children}</>
}

/** Loads the project a URL names and selects it (with its portfolio), the same
 *  way picking it on the Context Selector does. A project this user cannot see
 *  in the current mode falls back to "/". */
function RestoreProjectFromUrl({ projectId }: { projectId: string }) {
  const [missing, setMissing] = useState(false)
  useEffect(() => {
    let cancelled = false
    setMissing(false)
    void (async () => {
      const project = await getProjectById(projectId).catch(() => null)
      if (cancelled) return
      if (!project) { setMissing(true); return }
      const portfolio = project.portfolioId ? await getPortfolioById(project.portfolioId).catch(() => null) : null
      if (cancelled) return
      const store = useAppStore.getState()
      if (portfolio) store.selectPortfolio(portfolio)
      store.selectProject(project)
      if (store.managementContext !== 'project') store.setManagementContext('project')
    })()
    return () => { cancelled = true }
  }, [projectId])
  if (missing) return <Navigate to="/" replace />
  return <PageSkeleton />
}

/** Redirects to the selected project's detail page (used in project context) */
function ProjectOverviewRedirect() {
  const selectedProject = useAppStore((s) => s.selectedProject)
  if (!selectedProject) return <Navigate to="/" replace />
  return <Navigate to={`/projects/${selectedProject.id}`} replace />
}

/** Redirects to the selected project's team page */
function ProjectTeamRedirect() {
  const selectedProject = useAppStore((s) => s.selectedProject)
  if (!selectedProject) return <Navigate to="/" replace />
  return <Navigate to={`/projects/${selectedProject.id}/team`} replace />
}

/** Redirects to the selected project's additional-attributes page */
function ProjectAttributesRedirect() {
  const selectedProject = useAppStore((s) => s.selectedProject)
  if (!selectedProject) return <Navigate to="/" replace />
  return <Navigate to={`/projects/${selectedProject.id}/attributes`} replace />
}

/** Redirects to the selected project's locations page */
function ProjectLocationsRedirect() {
  const selectedProject = useAppStore((s) => s.selectedProject)
  if (!selectedProject) return <Navigate to="/" replace />
  return <Navigate to={`/projects/${selectedProject.id}/locations`} replace />
}

/** Redirects to the selected project's startup-checklist page */
function ProjectStartupChecklistRedirect() {
  const selectedProject = useAppStore((s) => s.selectedProject)
  if (!selectedProject) return <Navigate to="/" replace />
  return <Navigate to={`/projects/${selectedProject.id}/startup-checklist`} replace />
}

/** Preserves the :draftId when redirecting the legacy builder deep link into
 *  the Workflow Management module. */
function WorkflowBuilderRedirect() {
  const { draftId } = useParams()
  return <Navigate to={`/workflow-management/studio/builder/${draftId ?? ''}`} replace />
}

/** Determines default landing based on management context */
function DefaultRedirect() {
  const managementContext = useAppStore((s) => s.managementContext)
  if (managementContext === 'project') {
    return <ProjectOverviewRedirect />
  }
  return <Navigate to="dashboard" replace />
}

const IS_LOCALHOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

// Auth-skip must be OPT-IN, never inferred from the hostname alone — a deployed
// build served from a host that happens to be named localhost/127.0.0.1 must NOT
// bypass the login wall. Local dev keeps its convenience (skip the wall), but only
// under the Vite dev server (import.meta.env.DEV is false in any production build).
// On the INTERNAL build a fork/dev can force-skip with VITE_SKIP_AUTH=true for an
// explicit, auditable reason. On a PRODUCT build the login wall is mandatory and
// VITE_SKIP_AUTH is ignored (resolveSkipAuth enforces "product ⇒ auth always on").
const SKIP_AUTH = resolveSkipAuth({
  viteSkipAuth: (import.meta.env as Record<string, string | undefined>).VITE_SKIP_AUTH === 'true',
  isLocalhost: IS_LOCALHOST,
  isDev: import.meta.env.DEV,
})

// The login wall is now MANDATORY for every visitor (the access gate). The old
// VITE_REQUIRE_AUTH opt-in and the demo self-heal are gone: no anonymous access
// in any mode. VITE_REQUIRE_AUTH is still read for documentation/telemetry but
// no longer controls the wall — SKIP_AUTH (local Vite dev only) is the sole
// bypass, and even then only as a developer convenience.
const REQUIRE_AUTH = (import.meta.env as Record<string, string | undefined>).VITE_REQUIRE_AUTH === 'true'
void REQUIRE_AUTH

// A project share link (/join/<token>): keep the token for <AccessGate> to
// redeem after sign-in, and move the address to the root before the router
// reads it. Runs once, when the app module loads.
captureShareInvite()

// isDemoAuthAccount (types/demoProfiles): a shared @tessaractxpm.com demo
// session versus a real person who logged in through the wall.

/** Shared application route tree used by both demo and authenticated modes */
function AppRoutes() {
  return (
    <Suspense fallback={<PageSkeleton />}>
    <Routes>
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="my-work" element={<MyWorkPage />} />
      <Route path="my-work/inbox" element={<ActionInboxPage />} />
      <Route path="my-work/tasks" element={<MyTasksPage />} />
      <Route path="my-work/risks" element={<MyRisksPage />} />
      <Route path="my-work/calendar" element={<MyCalendarPage />} />
      <Route path="my-profile" element={<MyProfilePage />} />
      <Route path="projects" element={<ProjectList />} />
      <Route path="projects/:projectId" element={<ProjectDetail />} />
      <Route path="projects/:projectId/team" element={<ProjectTeamPage />} />
      <Route path="projects/:projectId/attributes" element={<ProjectAttributesPage />} />
      <Route path="projects/:projectId/locations" element={<ProjectLocationsPage />} />
      <Route path="projects/:projectId/startup-checklist" element={<ProjectStartupChecklistPage />} />
      <Route path="projects/:projectId/deploy" element={<SapDeploymentPage />} />
      <Route path="team" element={<ProjectTeamRedirect />} />
      <Route path="attributes" element={<ProjectAttributesRedirect />} />
      <Route path="project-locations" element={<ProjectLocationsRedirect />} />
      <Route path="startup-checklist" element={<ProjectStartupChecklistRedirect />} />
      <Route path="project-overview" element={<ProjectOverviewRedirect />} />
      <Route path="budget-releases" element={<BudgetReleases />} />
      <Route path="resource-consumption" element={<ResourceManagement />} />
      {/* Module-gated (#67a): mounted only when the module is enabled for this deployment. */}
      {/* Project-context materials page. NOTE: /supply-chain (no /materials) is
          intentionally NOT aliased here — that URL belongs to the SCP planning
          module (SupplyChainPage) mounted at supply-chain/* below, so the Supply
          Chain context lands on Planning Home rather than this project page. */}
      {isModuleEnabled('supplychain') && <Route path="materials" element={<SupplyChainManagement />} />}
      {isModuleEnabled('personnel') && <Route path="personnel" element={<PersonnelManagement />} />}
      <Route path="organizations" element={<OrganizationsHome />} />
      <Route path="organizations/resource-demands" element={<ResourceDemandsPage />} />
      <Route path="skill-approvals" element={<SkillApprovalsPage />} />
      <Route path="project-requests" element={<ProjectRequests />} />
      <Route path="project-requests/new" element={<NewProjectRequest />} />
      <Route path="project-requests/:requestId" element={<ProjectRequestDetail />} />
      <Route path="projects/new/:kind" element={<NewProjectRequest />} />
      <Route path="priorities" element={<Priorities />} />
      <Route path="roi" element={<FeatureGate feature="ppm.roi"><RoiModule /></FeatureGate>} />
      <Route path="lessons-learned" element={<FeatureGate feature="ppm.lessons-learned"><LessonsLearnedPage /></FeatureGate>} />
      <Route path="scenario-planning" element={<FeatureGate feature="ppm.scenario-planning"><ScenarioPlanning /></FeatureGate>} />
      {/* Workflow Management is a top-level module (#workflow-mgmt). The workflows_v3
          Studio + Builder now live under /workflow-management/*; the old /workflows
          paths redirect so every existing deep link and bookmark keeps working. */}
      {isModuleEnabled('workflow-management') && <Route path="workflow-management/*" element={<WorkflowManagementRoutesProvider />} />}
      <Route path="workflows" element={<Navigate to="/workflow-management/studio" replace />} />
      <Route path="workflows/builder" element={<Navigate to="/workflow-management/studio/builder" replace />} />
      <Route path="workflows/builder/:draftId" element={<WorkflowBuilderRedirect />} />
      <Route path="workflows-legacy" element={<Workflows />} />
      <Route path="analysis" element={<Analysis />} />
      <Route path="risks" element={<RiskRegister />} />
      <Route path="milestone-analyzer" element={<FeatureGate feature="ppm.milestone-analyzer"><MilestoneAnalyzer /></FeatureGate>} />
      <Route path="programs/review" element={<ProgramReview />} />
      <Route path="programs/:programId/review" element={<ProgramReview />} />

      {/* Project-context tools */}
      <Route path="gantt" element={<SchedulingPage />} />
      <Route path="finance" element={<BaselineManagement />} />
      <Route path="forecasts" element={<ForecastManagement />} />
      <Route path="rates" element={<RatesManagement />} />
      <Route path="evm" element={<EvmWorkbench />} />

      {/* Agile Delivery (Hybrid Waterfall/Agile). All five stay registered so
          the nav-coverage gates map each route; RequireCapability redirects
          when the agile.enabled capability is turned off in Configuration. */}
      <Route path="backlog" element={<RequireCapability flag="agile.enabled" redirectTo="/project-overview"><BacklogPage /></RequireCapability>} />
      <Route path="backlog/item/:itemId" element={<RequireCapability flag="agile.enabled" redirectTo="/project-overview"><WorkItemDetailPage /></RequireCapability>} />
      <Route path="board" element={<RequireCapability flag="agile.enabled" redirectTo="/project-overview"><BoardPage /></RequireCapability>} />
      <Route path="sprints" element={<RequireCapability flag="agile.enabled" redirectTo="/project-overview"><SprintsPage /></RequireCapability>} />
      <Route path="roadmap" element={<RequireCapability flag="agile.enabled" redirectTo="/project-overview"><RoadmapPage /></RequireCapability>} />
      <Route path="agile-insights" element={<RequireCapability flag="agile.enabled" redirectTo="/project-overview"><AgileInsightsPage /></RequireCapability>} />
      {isModuleEnabled('clm') && <Route path="contracts/*" element={<CLMRoutesProvider />} />}
      {isModuleEnabled('timesheet') && <Route path="timesheet/*" element={<TimesheetRoutesProvider />} />}
      <Route path="reports" element={<ReportsPage />} />
      {/* Analytics Studio (#kg): /analytics stays the Conversational Analytics
          workspace so every existing link survives; /analytics/knowledge-graph
          adds the governed semantic model that grounds the analytics agent. */}
      <Route path="analytics/*" element={<AnalyticsRoutesProvider />} />
      <Route path="hierarchy" element={<WbsHierarchyPage />} />
      <Route path="hierarchy/linkages" element={<HierarchyLinkagesPage />} />
      <Route path="implementation" element={<ImplementationHub />} />
      <Route path="notifications" element={<NotificationsCockpit />} />
      <Route path="help" element={<HelpCenterPage />} />
      <Route path="sap-integration" element={<SapIntegrationPage />} />
      <Route path="erp-sync" element={<ErpSyncPage />} />
      <Route path="project-fx" element={<CurrencyRatesPage />} />
      <Route path="estimate-import" element={<EstimateImportPage />} />
      {isModuleEnabled('supplychain') && <Route path="supply-chain/*" element={<SupplyChainPage />} />}

      {/* Cost Management is a top-level module (#cost-mgmt): universal ledger,
          pools + rate segments, allocations, burden rates, cost transfers, and
          incurred cost reporting. The CAS rates page moved here from the
          Organization context; /organization/rates redirects so existing deep
          links keep working. */}
      {isModuleEnabled('cost-management') && <Route path="cost/*" element={<CostManagementRoutesProvider />} />}

      {/* CRM Studio (#crm): accounts, contacts, and the interaction log now;
          leads, pipeline, Customer 360, CPQ, and the customer portal in later
          phases. Reads the SAP customer master live and links accounts by
          KUNNR rather than mirroring it. */}
      {isModuleEnabled('crm') && <Route path="crm/*" element={<CrmRoutesProvider />} />}

      {/* IT Ticketing Studio (#it-ticketing): help-desk ticket tracking ported
          from the standalone support-ticketing app — requests, workflow
          approvals, delegations, and reporting. */}
      {isModuleEnabled('it-ticketing') && <Route path="it-ticketing/*" element={<ItTicketingRoutesProvider />} />}

      {/* Organizational Management context routes */}
      <Route path="organization/rates" element={<Navigate to="/cost/rates" replace />} />

      {/* Configuration context routes — accessible in any mode for demo purposes */}
      <Route path="config" element={<ConfigDashboard />} />
      <Route path="config/instance-setup" element={<ConfigInstanceSetup />} />
      <Route path="config/client-setup" element={<ConfigClientSetup />} />
      <Route path="config/users" element={<ConfigUserManagement />} />
      <Route path="config/access" element={<ConfigAccessRequests />} />
      <Route path="config/org-directory" element={<ConfigOrgDirectory />} />
      <Route path="config/roles" element={<ConfigRoleAssignments />} />
      <Route path="config/policies" element={<ConfigPolicies />} />
      <Route path="config/sops" element={<ConfigSops />} />
      <Route path="config/audit" element={<ConfigAuditLog />} />
      <Route path="config/ui-mappings" element={<ConfigUiMappings />} />
      <Route path="config/project-lifecycle" element={<ConfigProjectLifecycle />} />
      <Route path="config/expertise" element={<ConfigExpertise />} />
      <Route path="config/disciplines" element={<ConfigDisciplines />} />
      <Route path="config/resource-planning" element={<ConfigResourcePlanning />} />
      <Route path="config/resource-fields" element={<ConfigResourceFields />} />
      <Route path="config/tools" element={<ConfigTools />} />
      <Route path="config/locations" element={<ConfigLocations />} />
      <Route path="config/cost-planning-accounts" element={<ConfigCostPlanningAccounts />} />
      <Route path="config/chart-of-accounts" element={<ConfigChartOfAccounts />} />
      <Route path="config/demo-library" element={<ConfigDemoLibrary />} />
      <Route path="config/feature-flags" element={<ConfigFeatureFlags />} />
      <Route path="config/capabilities" element={<ConfigCapabilities />} />
      <Route path="onboarding" element={<OnboardingPage />} />
      <Route path="config/certifications" element={<ConfigCertifications />} />
      <Route path="config/project-roles" element={<ConfigProjectRoles />} />
      <Route path="config/budget-categories" element={<ConfigBudgetCategories />} />
      <Route path="config/award-value-categories" element={<ConfigAwardValueCategories />} />
      <Route path="config/manufacturing-time" element={<ConfigManufacturingTime />} />
      <Route path="config/time-evaluation-rules" element={<ConfigTimeEvaluationRules />} />
      <Route path="config/wbs-attribute-library" element={<ConfigWbsAttributeLibrary />} />
      <Route path="config/sap-integration" element={<SapIntegrationPage />} />
      <Route path="config/org-import" element={<ConfigOrgImport />} />

      <Route path="developer/api-explorer" element={<ApiExplorer />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
    </Suspense>
  )
}

/** The authenticated app shell + route tree. Shared by the auth-skip (local dev)
 *  path and the gated (deployed) path so the shell markup lives in one place. */
function ShellRoutes() {
  return (
    <>
      <Toaster position="bottom-right" richColors closeButton />
      {/* Per-user module entitlements (migration 00521): a restricted user is
          default-denied on any path outside their allowed modules. No-op for
          unrestricted users and app admins. */}
      <ModuleRouteGuard>
        <Routes>
          <Route path="/" element={<ContextSelector />} />
          <Route path="/portfolios/new" element={<Suspense fallback={<PageSkeleton />}><NewPortfolio /></Suspense>} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppShell><AgileOnlyRouteGuard><AppRoutes /></AgileOnlyRouteGuard></AppShell>
              </ProtectedRoute>
            }
          />
        </Routes>
      </ModuleRouteGuard>
    </>
  )
}

export default function App() {
  // Initialize Supabase auth — restores session from storage
  const { initialize, session, loading: authLoading, user } = useAuthStore()
  const setCurrentUser = useAppStore((s) => s.setCurrentUser)
  const appMode = useAppStore((s) => s.appMode)
  const modeRequiresAuth = appMode !== 'demo'

  useEffect(() => {
    initialize().then(() => {
      // Local-dev convenience ONLY: under the explicit auth-skip (localhost Vite
      // dev, or VITE_SKIP_AUTH=true), keep the demo auto-sign-in so `npm run dev`
      // lands in the demo without a login. On any real deployment SKIP_AUTH is
      // false, so this never runs and the login wall is enforced for everyone —
      // there is no anonymous demo bypass anymore.
      if (SKIP_AUTH) {
        const { session: currentSession } = useAuthStore.getState()
        if (!currentSession && useAppStore.getState().appMode === 'demo') {
          useAuthStore.getState().signInAsDemo(getDemoProfile(useAppStore.getState().demoProfileId))
        }
      }
    })
  }, [initialize])

  // A real person who logs in through the wall must land in a real working
  // environment, not the demo. When the persisted appMode is a stale 'demo' but
  // the authenticated account is a real (non-demo) account, move them to
  // Sandbox so their true roster identity + role bind (this is what makes the
  // GlobalAdmin superuser a working admin, and gives every real user their own
  // data instead of demo data their role cannot see). Keyed on `user` only so a
  // deliberate switch INTO demo (which swaps to a demo-account session) is not
  // fought by this effect.
  useEffect(() => {
    if (user && !isDemoAuthAccount(user.email) && useAppStore.getState().appMode === 'demo') {
      useAppStore.getState().setAppMode('sandbox')
    }
  }, [user])

  // Sync the 3 admin catalogs (expertise / certifications / cost-planning) from
  // Supabase into the localStorage cache on app boot. The libs that own these
  // catalogs read synchronously from localStorage, so this hydrates the cache
  // before any consumer (AddResourceDialog, ConfigExpertise, ODC forecast,
  // etc.) reads. No-op when VITE_DATA_SOURCE !== 'supabase'.
  useEffect(() => {
    void Promise.all([
      import('@/lib/expertiseCatalog').then((m) => m.syncExpertiseCatalogFromSupabase()),
      import('@/lib/certificationsCatalog').then((m) => m.syncCertificationsCatalogFromSupabase()),
      import('@/lib/costPlanningAccounts').then((m) => m.syncCostPlanningAccountsFromSupabase()),
      import('@/lib/toolsCatalog').then((m) => m.syncToolsCatalogFromSupabase()),
      import('@/lib/disciplinesCatalog').then((m) => m.syncDisciplinesCatalogFromSupabase()),
    ])
    // The module capability flag catalog (agile.enabled, ...) is no longer
    // registered here: this effect runs for every visitor, including the login
    // wall with no session, and the anon upsert was refused by RLS on every
    // load (issue #30). <AccessGate> syncs it once the signed-in identity is
    // known, writing only for an approved administrator.
  }, [])

  // Bridge Supabase user → appStore currentUser for the ABAC engine
  useEffect(() => {
    if (user) {
      const meta = user.user_metadata ?? {}
      const orgId = meta.organization_id || null
      // Persist org ID so dataService can filter queries by organization
      try {
        if (orgId) localStorage.setItem('ppm-organization-id', orgId)
        else localStorage.removeItem('ppm-organization-id')
      } catch { /* ignore */ }
      // In DEMO mode the picked persona is the authoritative identity — it is
      // restored into currentUser from the persisted persona snapshot in
      // appStore init and maintained by the PersonaSwitcher. Bridging the demo
      // auth account into currentUser here would overwrite that on every load,
      // reverting the persona to the generic demo user (e.g. "Demo User
      // (Aerospace & Defense)") on refresh and making persona-bound surfaces
      // (My Profile) reload the wrong person. So only bridge Supabase auth →
      // currentUser in real-auth modes; demo identity flows from the persona.
      if (modeRequiresAuth) {
        // Auth metadata is the FALLBACK identity (fresh sign-up, or an account not
        // yet provisioned into the org roster).
        const metadataIdentity = {
          id: user.id,
          name: meta.full_name || user.email?.split('@')[0] || 'User',
          role: meta.ppm_role || 'PortfolioManager',
          organizationId: orgId,
          organizationName: meta.organization_name || null,
          assignedPortfolios: meta.assigned_portfolios || [],
          costCenterCode: meta.cost_center || '75-04-500',
          clearance: meta.clearance || 'None',
          department: meta.department || 'General',
          initials: (meta.full_name || user.email || 'U')
            .split(' ')
            .map((w: string) => w[0])
            .join('')
            .toUpperCase()
            .slice(0, 2),
        }
        // Production-edition bind: the logged-in account IS its real `users` roster
        // row — org placement, manager (approval chain), access role, cost center
        // come from the DB, not auth metadata ("tie login in with the DB"). Resolve
        // by email; fall back to metadata when no roster row matches.
        const email = user.email
        if (email) {
          void fetchRosterIdentityByEmail(email, user.id)
            .then((row) => {
              if (row) {
                const bound = rosterRowToCurrentUser(row)
                setCurrentUser(bound)
                useTimesheetStore.getState().setCurrentEmployeeId(row.id)
                useAppStore.getState().setTimesheetRoleOverride(row.systemRole ?? null)
                try {
                  // Pin the active org to the user's home org so they start scoped to
                  // it and see its projects. Non-admins resolve their org via
                  // bound.organizationId; a GlobalAdmin resolves to null but still
                  // belongs to a primary org (row.primaryOrganizationId) — pin to that
                  // so they land in their own org (they can still switch to "All
                  // organizations" via the org switcher). Only falls back to all-org
                  // when the account has no home org at all.
                  const pinnedOrgId = bound.organizationId ?? row.primaryOrganizationId ?? null
                  useAppStore.getState().setActiveOrganization(pinnedOrgId)
                } catch { /* ignore */ }
              } else {
                // No roster row for this authenticated account. What happens next
                // is the SSO JIT policy (#116): a PRODUCT fork enforces it, while
                // internal/sandbox keeps the metadata fallback for test convenience.
                const outcome = resolveRosterBindOutcome(false, isProductSurface(), ssoJitMode())
                if (outcome === 'provision') {
                  // JIT-create seam: a fork that self-provisions wires its backend
                  // here (e.g. a Supabase Edge Function mapping the SSO claims → a
                  // roster row, then re-running fetchRosterIdentityByEmail). Until
                  // that's wired it safe-denies rather than granting metadata access.
                  console.warn('[auth] SSO JIT provision requested but no provisioning backend is wired; denying.')
                  void useAuthStore.getState().denyAccess(
                    'Your account could not be provisioned automatically. Contact your administrator.',
                  )
                } else if (outcome === 'deny') {
                  void useAuthStore.getState().denyAccess(
                    'Your account is not provisioned for this system yet. Contact your administrator.',
                  )
                } else {
                  setCurrentUser(metadataIdentity) // internal/sandbox convenience
                }
              }
            })
            .catch(() => setCurrentUser(metadataIdentity))
        } else {
          setCurrentUser(metadataIdentity)
        }
      }
    }
  }, [user, setCurrentUser, modeRequiresAuth])

  // Initialize authorization engine
  useAuthInitialize()

  // Public shares: standalone surfaces, no auth and no app shell, in every
  // mode. /f/:slug opens + submits a shared form; /s/:token renders a
  // read-only schedule snapshot; /b/:token renders a read-only snapshot of a
  // project's agile views (backlog, board, sprints, roadmap); /r/:token renders a read-only
  // snapshot of a project's RAID Log. Anyone with the link can view.
  // /i/:token is a form invitation: a named outsider, link plus passcode,
  // checked by the server (web/api/invite), never a public link.
  // /test-portal is the tester portal: testers who are not Tesseract users sign in
  // there with their own tester account and execute test scripts.
  if (
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/f/') ||
      window.location.pathname.startsWith('/i/') ||
      window.location.pathname.startsWith('/s/') ||
      window.location.pathname.startsWith('/b/') ||
      window.location.pathname.startsWith('/r/') ||
      window.location.pathname === '/test-portal' ||
      window.location.pathname.startsWith('/test-portal/'))
  ) {
    return (
      <>
        <Toaster position="bottom-right" richColors closeButton />
        <Routes>
          <Route path="/f/:slug" element={<Suspense fallback={<PageSkeleton />}><PublicFormPage /></Suspense>} />
          <Route path="/i/:token" element={<Suspense fallback={<PageSkeleton />}><InviteFormPage /></Suspense>} />
          <Route path="/s/:token" element={<Suspense fallback={<PageSkeleton />}><SharedSchedulePage /></Suspense>} />
          <Route path="/b/:token" element={<Suspense fallback={<PageSkeleton />}><SharedAgilePage /></Suspense>} />
          <Route path="/r/:token" element={<Suspense fallback={<PageSkeleton />}><SharedRaidPage /></Suspense>} />
          <Route path="/test-portal/*" element={<Suspense fallback={<PageSkeleton />}><TesterPortalPage /></Suspense>} />
        </Routes>
      </>
    )
  }

  // Auth-skip (local Vite dev, or an explicit VITE_SKIP_AUTH fork): render the
  // app without the wall or gate. Never true on a real deployment.
  if (SKIP_AUTH) {
    return <ShellRoutes />
  }

  // Restoring the session — show a spinner rather than flashing the login wall.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-3 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
      </div>
    )
  }

  // The login wall — MANDATORY for every visitor in every mode (including demo).
  // No session, no app. This is the front door of the lock.
  if (!session) {
    return <LoginPage />
  }

  // Authenticated: the AccessGate admits only users on the approved list; a
  // pending or suspended account gets a blocking screen instead of the app.
  return (
    <AccessGate>
      <ShellRoutes />
    </AccessGate>
  )
}
